import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import rrulePlugin from '@fullcalendar/rrule'
//import googleCalendarPlugin from "@fullcalendar/google-calendar";
//import initTooltip from "./tooltip";

// https://fullcalendar.io/docs/customButtons

/**
 * Monthly View
 * Docs: https://fullcalendar.io/docs/month-view
 */
export default function initEventCalendarMonth() {
  const calendarEl = document.getElementById("mg-calendar");

  // Add CSS to hide anchor links and make them non-clickable
  if (!document.getElementById('mgcalendar-styles')) {
    const style = document.createElement('style');
    style.id = 'mgcalendar-styles';
    style.textContent = `
      /* Screen reader only text for better navigation */
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
      

    `;
    document.head.appendChild(style);
  }

  // Get html lang attribute
  const htmlLang = document.documentElement.lang;

  // Create prev button initial text as ${prevMonth} ${currentYear}
  let prevButtonText = "Previous";
  let nextButtonText = "Next";

  // Filter state variables - moved to main scope for access by button handlers
  let selectedRegion = 'all';
  let selectedCategory = 'all';
  let filtersInitialized = false; // Flag to prevent viewDidMount from running multiple times
  let allEvents = []; // Store ALL events from all months
  let isApplyingFilters = false; // Flag to prevent infinite loops

  // If the calendar element doesn't exist, don't try to initialize the calendar
  if (!calendarEl) {
    return;
  }

  // Get search value from query string
  const searchParams = new URLSearchParams(window.location.search);
  const searchValue = searchParams.get("search");
  // Get search input and set value
  const searchInput = document.getElementById("mg-calendar-search");
  if (searchInput) {
    searchInput.value = searchValue;
  }

  function updateCustomButtons() {
    const prevButton = document.querySelector(".fc-prevCustom-button");
    const nextButton = document.querySelector(".fc-nextCustom-button");

    prevButton.innerText = "Previous";
    nextButton.innerText = "Next";
  }

  // Function to apply both filters together - moved to main scope for access by button handlers
  function applyFilters() {
    if (!allEvents || allEvents.length === 0) {
      return;
    }

    // Set flag to prevent infinite loops
    isApplyingFilters = true;

    // If both filters are "all", show only current month events
    if (selectedRegion === 'all' && selectedCategory === 'all') {
      // No need to do anything - current month events are already shown
      isApplyingFilters = false;
      return;
    } else {
      // Apply filtering logic to all events
      const filteredEvents = allEvents.filter(event => {
        // Skip null or invalid events
        if (!event || !event.start) {
          return false;
        }
        
        // Region filter logic
        let regionMatch = false;
        if (selectedRegion === 'all') {
          regionMatch = true; // All regions match
        } else {
          // Check if event region matches selected region
          const eventRegion = event.region;
          const selectedRegionStr = String(selectedRegion);
          
          // Handle both single region and multiple regions (array)
          if (Array.isArray(eventRegion)) {
            // Event has multiple regions - check if selected region is in the array
            regionMatch = eventRegion.some(region => String(region) === selectedRegionStr);
          } else {
            // Event has single region - check exact match
            const eventRegionStr = String(eventRegion);
            regionMatch = eventRegionStr === selectedRegionStr;
          }
        }

        // Category filter logic
        let categoryMatch = false;
        if (selectedCategory === 'all') {
          categoryMatch = true; // All categories match
        } else {
          // Check if event category matches selected category
          const eventCategoryId = event.categoryId;
          
          // Handle both single category and multiple categories (array)
          if (Array.isArray(eventCategoryId)) {
            // Event has multiple categories - check if selected category is in the array
            categoryMatch = eventCategoryId.some(category => String(category) === String(selectedCategory));
          } else {
            // Event has single category - check exact match
            categoryMatch = String(eventCategoryId) === String(selectedCategory);
          }
        }
        
        // Final result - AND logic between region and category
        return regionMatch && categoryMatch;
      });
      


      // Update calendar with filtered events
      calendar.removeAllEvents();
      filteredEvents.forEach(event => {
        // Only add valid events
        if (event && event.start) {
          calendar.addEvent(event);
        }
      });

      // Force calendar to refresh the view to show all filtered events
      // This ensures events outside the current visible range are properly displayed
      const currentDate = calendar.getDate();
      calendar.gotoDate(currentDate);

    }

    // Reset flag after a short delay
    setTimeout(() => {
      isApplyingFilters = false;
    }, 100);
  }

  // Function to ensure proper table headers for accessibility
  function ensureTableHeaders() {
    // Find the main calendar table
    const calendarTable = document.querySelector('.fc-daygrid-body table');
    if (!calendarTable) return;

    // Ensure the table has proper role and label
    calendarTable.setAttribute('role', 'table');
    calendarTable.setAttribute('aria-label', 'Calendar grid for current month');

    // Find or create the table header row
    let thead = calendarTable.querySelector('thead');
    if (!thead) {
      thead = document.createElement('thead');
      calendarTable.insertBefore(thead, calendarTable.firstChild);
    }
    
    // Add custom class and remove problematic role to prevent unwanted styling
    thead.classList.add('mgcalendar__accessibility-thead');
    thead.removeAttribute('role');

    // Find or create the header row
    let headerRow = thead.querySelector('tr');
    if (!headerRow) {
      headerRow = document.createElement('tr');
      thead.appendChild(headerRow);
    }

    // Clear existing headers and create new ones
    headerRow.innerHTML = '';

    // Get the day headers from FullCalendar's existing structure
    const dayHeaders = document.querySelectorAll('.fc-col-header-cell');
    
    // Create proper <th> elements for each day
    dayHeaders.forEach((dayHeader, index) => {
      const th = document.createElement('th');
      th.setAttribute('scope', 'col');
      th.setAttribute('role', 'columnheader');
      
      // Get the day name from the existing header
      const dayName = dayHeader.textContent.trim();
      th.textContent = dayName;
      
      // Add screen reader text for better context
      const srText = document.createElement('span');
      srText.className = 'sr-only';
      srText.textContent = `Column for ${dayName}`;
      th.appendChild(srText);
      
      headerRow.appendChild(th);
    });

    // Ensure the table body has proper role
    const tbody = calendarTable.querySelector('tbody');
    if (tbody) {
      tbody.setAttribute('role', 'rowgroup');
      // Add custom class for styling control
      tbody.classList.add('mgcalendar__accessibility-tbody');
    }

    // Add proper roles to table rows
    const tableRows = tbody ? tbody.querySelectorAll('tr') : [];
    tableRows.forEach((row, rowIndex) => {
      row.setAttribute('role', 'row');
      row.setAttribute('aria-label', `Week ${rowIndex + 1}`);
      
      // Ensure each cell has proper role for accessibility while maintaining styling
      const cells = row.querySelectorAll('td');
      cells.forEach((cell, cellIndex) => {
        // Use 'presentation' role to maintain existing CSS styling
        cell.setAttribute('role', 'presentation');
        cell.setAttribute('aria-label', `${dayHeaders[cellIndex]?.textContent.trim() || 'Day'} ${rowIndex + 1}`);
        
        // Add custom class for new accessibility cells so they can be styled separately
        cell.classList.add('mgcalendar__accessibility-cell');
      });
    });

    // Hide the original FullCalendar headers since we now have proper <th> elements
    dayHeaders.forEach(header => {
      header.style.display = 'none';
    });
  }

  // Function to clean up any remaining anchor links
  function cleanupAnchorLinks() {
    // Find and replace all day header anchors
    const dayHeaderAnchors = document.querySelectorAll('.fc-col-header-cell-cushion');
    dayHeaderAnchors.forEach(anchor => {
      if (anchor.tagName === 'A') {
        const spanElement = document.createElement('span');
        spanElement.className = anchor.className;
        spanElement.textContent = anchor.textContent;
        spanElement.setAttribute('aria-label', anchor.getAttribute('aria-label'));
        anchor.parentNode.replaceChild(spanElement, anchor);
      }
    });

    // Find and replace all day number anchors
    const dayNumberAnchors = document.querySelectorAll('.fc-daygrid-day-number');
    dayNumberAnchors.forEach(anchor => {
      if (anchor.tagName === 'A') {
        const spanElement = document.createElement('span');
        spanElement.className = anchor.className;
        spanElement.textContent = anchor.textContent;
        spanElement.setAttribute('aria-label', anchor.getAttribute('aria-label'));
        spanElement.setAttribute('id', anchor.getAttribute('id'));
        anchor.parentNode.replaceChild(spanElement, anchor);
      }
    });

    // Clean up any duplicate spans that might have been created
    const dayNumberContainers = document.querySelectorAll('.fc-daygrid-day-top');
    dayNumberContainers.forEach(container => {
      const dayNumberSpans = container.querySelectorAll('.fc-daygrid-day-number');
      if (dayNumberSpans.length > 1) {
        // Keep only the first span, remove the rest
        for (let i = 1; i < dayNumberSpans.length; i++) {
          dayNumberSpans[i].remove();
        }
      }
    });
  }



  const calendar = new Calendar(calendarEl, {
    timeZone: "UTC",
    locale: htmlLang,
    plugins: [dayGridPlugin, listPlugin, rrulePlugin],
    initialView: "dayGridMonth",
    height: "auto",
    forceEventDuration: true,
    fixedWeekCount: false,
    // Ensure past events are shown
    showNonCurrentDates: true,
    // Don't hide past events
    hidePastEvents: false,
    // Show events from other months in the current view
    dayMaxEvents: true,
    // Ensure all events are visible
    eventDisplay: 'block',
    // Add accessibility improvements
    a11yLabel: {
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      today: 'Today',
      dayGridMonth: 'Month view',
      listWeek: 'List view'
    },
    // Add proper table headers for accessibility
    dayHeaderFormat: { weekday: "long" },
    // Ensure proper table structure for accessibility
    dayHeaderDidMount: function(arg) {
      // Convert day header anchors to spans for better accessibility
      const anchorElement = arg.el.querySelector('.fc-col-header-cell-cushion');
      if (anchorElement && anchorElement.tagName === 'A') {
        const spanElement = document.createElement('span');
        spanElement.className = anchorElement.className;
        spanElement.textContent = anchorElement.textContent;
        spanElement.setAttribute('aria-label', anchorElement.getAttribute('aria-label'));
        anchorElement.parentNode.replaceChild(spanElement, anchorElement);
      }
      
      // Ensure proper table structure is created
      setTimeout(() => {
        ensureTableHeaders();
      }, 100);
    },
    // Add ARIA labels for better screen reader support
    dayCellDidMount: function(arg) {
      const date = arg.date;
      const dayOfWeek = date.toLocaleDateString(htmlLang, { weekday: 'long' });
      const dayOfMonth = date.getDate();
      const month = date.toLocaleDateString(htmlLang, { month: 'long' });
      const year = date.getFullYear();
      
      // Add ARIA label to each day cell
      arg.el.setAttribute('aria-label', `${dayOfWeek}, ${month} ${dayOfMonth}, ${year}`);
      
      // Add role for better accessibility
      arg.el.setAttribute('role', 'gridcell');
      
      // Convert day number anchors to spans
      const dayNumberAnchor = arg.el.querySelector('.fc-daygrid-day-number');
      if (dayNumberAnchor && dayNumberAnchor.tagName === 'A') {
        const spanElement = document.createElement('span');
        spanElement.className = dayNumberAnchor.className;
        spanElement.textContent = dayNumberAnchor.textContent;
        spanElement.setAttribute('aria-label', dayNumberAnchor.getAttribute('aria-label'));
        spanElement.setAttribute('id', dayNumberAnchor.getAttribute('id'));
        dayNumberAnchor.parentNode.replaceChild(spanElement, dayNumberAnchor);
      }
    },
    headerToolbar: {
      left: "title,regionFilter",
      center: "",
      right: "prevCustom,nextCustom",
    },
    customButtons: {
      regionFilter: {
        text: "Region",
      },
      prevCustom: {
        text: prevButtonText,
        click: function () {
          calendar.prev();
          updateCustomButtons();
        },
      },
      nextCustom: {
        text: nextButtonText,
        click: function () {
          calendar.next();
          updateCustomButtons();
        },
      },
    },
    firstDay: 0,
    navLinks: false, // Disable navigation links to prevent anchor creation
    eventTimeFormat: {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    },
    eventSources: [
      {
        id: "events",
        url: "//" + window.location.host + "/wp-json/custom/v1/calendar-events",
        method: "GET",
        extraParams: {
          _t: Date.now() // Cache-busting timestamp
        },
        success: function(events) {
          // Filter out any null or invalid events before they reach FullCalendar
          if (Array.isArray(events)) {
            const validEvents = events.filter(event => event && event.start && event.title);
            
            // Filter out the last occurrence for events where hideLastOccurringEvent is true
            const eventsByBaseId = {};
            validEvents.forEach(event => {
              const baseEventId = event.id;
              if (!eventsByBaseId[baseEventId]) {
                eventsByBaseId[baseEventId] = [];
              }
              eventsByBaseId[baseEventId].push(event);
            });
            
            // Find and mark the last occurrence to exclude
            const eventsToExclude = [];
            Object.keys(eventsByBaseId).forEach(baseEventId => {
              const eventGroup = eventsByBaseId[baseEventId];
              if (eventGroup.length === 0) return;
              
              const hideLastOccurring = eventGroup[0].hideLastOccurringEvent;
              if (hideLastOccurring === true || hideLastOccurring === "1" || hideLastOccurring === 1) {
                // Find the event with the latest start date
                let lastEvent = eventGroup[0];
                let latestDate = new Date(eventGroup[0].start);
                
                eventGroup.forEach(event => {
                  const eventDate = new Date(event.start);
                  if (eventDate > latestDate) {
                    latestDate = eventDate;
                    lastEvent = event;
                  }
                });
                
                // Mark this event to be excluded
                eventsToExclude.push(lastEvent);
              }
            });
            
            // Filter out the excluded events by comparing start date and id
            const filteredEvents = validEvents.filter(event => {
              return !eventsToExclude.some(excludedEvent => 
                excludedEvent.id === event.id && 
                excludedEvent.start === event.start
              );
            });
            
            // Store ALL events in allEvents array for filtering
            allEvents = filteredEvents;
            
            return filteredEvents;
          }
          return events;
        }
      },
    ],
    eventContent: function (arg) {
      const viewType = arg.view.type;
      const monthViewEntry = document.createElement("div");
      const toolTip = document.createElement("div");
      const eventTimeEl = document.createElement("div");
      const allDay = arg.event.allDay;
      const eventContent = arg.event.extendedProps.content;
      const featuredImage = arg.event.extendedProps.featuredImage;
      const category = arg.event.extendedProps.category;
      const salesForceImage = arg.event.extendedProps.salesForceImage;
      const isNationalHoliday = arg.event.extendedProps.isNationalHoliday;
      const status = arg.event.extendedProps.status;
      const officeClosureEvent = arg.event.extendedProps.officeClosureEvent;

      if (status === "Completed") {
        monthViewEntry.classList.add("mgcalendar__event--completed");
      }

      let eventDate = new Date(arg.event.start);
      eventDate = eventDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      // event time
      if (allDay == false) {
        let eventTime = "";
        if (arg.event.start == null) {
          let start = new Date(arg.event.startTime);
          let startHour = start.toLocaleTimeString();
          let end = new Date(arg.event.endTime);
          let endHour = end.toLocaleTimeString();
          eventTime = `${startHour}-${endHour}`;
        } else {
          let start = new Date(arg.event.start);
          let startHour = start.toLocaleTimeString();
          let end = new Date(arg.event.end);
          let endHour = end.toLocaleTimeString();
          eventTime = `${startHour}-${endHour}`;
        }
        eventTimeEl.innerHTML = eventTime;
      } else {
        eventTimeEl.innerHTML = "All day event";
      }
      const tooltipId = Math.random().toString(36).substring(7);
      // Format title with office-closed class if officeClosureEvent is true
      // ACF boolean fields can return true, "1", or 1, so check for all cases
      const eventTitle = (officeClosureEvent === true || officeClosureEvent === "1" || officeClosureEvent === 1)
        ? `<span class="office-closed">${arg.event.title}</span>`
        : arg.event.title;
      monthViewEntry.innerHTML = `
        <div class="tooltip" data-tooltip-content="#tooltip_content-${tooltipId}">
        ${eventTitle}
        ${arg.event.extendedProps.external ? `<span class="mgcalendar__tooltip-external"><?xml version="1.0" encoding="iso-8859-1"?> <svg height="800px" width="800px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 511.998 511.998" xml:space="preserve"> <path style="fill:#3D6DEB;" d="M480.279,287.716c17.517,0,31.718-14.201,31.718-31.718V31.729c0-1.051-0.055-2.1-0.159-3.144 c-0.042-0.448-0.129-0.884-0.19-1.326c-0.082-0.581-0.152-1.165-0.266-1.742c-0.104-0.518-0.245-1.017-0.374-1.527 c-0.125-0.495-0.23-0.994-0.381-1.484c-0.152-0.501-0.34-0.985-0.516-1.476c-0.173-0.484-0.334-0.975-0.533-1.453 c-0.19-0.459-0.412-0.899-0.624-1.347c-0.23-0.491-0.45-0.985-0.706-1.465c-0.23-0.429-0.491-0.835-0.742-1.252 c-0.281-0.472-0.548-0.949-0.854-1.408c-0.309-0.461-0.651-0.892-0.983-1.336c-0.288-0.385-0.556-0.78-0.863-1.155 c-0.639-0.776-1.311-1.522-2.021-2.235c-0.027-0.03-0.049-0.059-0.078-0.089c-0.034-0.034-0.072-0.061-0.108-0.095 c-0.706-0.702-1.446-1.37-2.214-2.002c-0.379-0.309-0.776-0.579-1.163-0.869c-0.44-0.33-0.871-0.67-1.33-0.977 c-0.459-0.307-0.935-0.573-1.406-0.854c-0.417-0.25-0.827-0.512-1.256-0.742c-0.478-0.256-0.968-0.472-1.457-0.702 c-0.45-0.214-0.892-0.438-1.355-0.63c-0.474-0.195-0.956-0.353-1.436-0.524c-0.497-0.18-0.99-0.368-1.497-0.524 c-0.476-0.144-0.962-0.25-1.444-0.37c-0.52-0.131-1.034-0.277-1.567-0.383c-0.554-0.11-1.114-0.173-1.673-0.256 c-0.465-0.066-0.922-0.154-1.396-0.201c-1.023-0.101-2.053-0.154-3.081-0.156c-0.017,0-0.034-0.002-0.047-0.002H256.002 c-17.517,0-31.718,14.201-31.718,31.718s14.201,31.718,31.718,31.718h147.706L9.291,457.853c-12.387,12.387-12.387,32.469,0,44.856 c6.194,6.194,14.311,9.289,22.429,9.289s16.236-3.096,22.427-9.291l394.414-394.414v147.705 C448.561,273.515,462.762,287.716,480.279,287.716z"/> </svg></span>` : ""}
        </div>`;

      toolTip.classList.add("mgcalendar__tooltip");
      toolTip.innerHTML = `
      <div id="tooltip_content-${tooltipId}">
        <div class="mgcalendar__tooltip-header">
          <div class="mgcalendar__tooltip-title">
            ${eventTitle}
            ${arg.event.extendedProps.external ? `<span class="mgcalendar__tooltip-external"><?xml version="1.0" encoding="iso-8859-1"?> <svg height="800px" width="800px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 511.998 511.998" xml:space="preserve"> <path style="fill:#3D6DEB;" d="M480.279,287.716c17.517,0,31.718-14.201,31.718-31.718V31.729c0-1.051-0.055-2.1-0.159-3.144 c-0.042-0.448-0.129-0.884-0.19-1.326c-0.082-0.581-0.152-1.165-0.266-1.742c-0.104-0.518-0.245-1.017-0.374-1.527 c-0.125-0.495-0.23-0.994-0.381-1.484c-0.152-0.501-0.34-0.985-0.516-1.476c-0.173-0.484-0.334-0.975-0.533-1.453 c-0.19-0.459-0.412-0.899-0.624-1.347c-0.23-0.491-0.45-0.985-0.706-1.465c-0.23-0.429-0.491-0.835-0.742-1.252 c-0.281-0.472-0.548-0.949-0.854-1.408c-0.309-0.461-0.651-0.892-0.983-1.336c-0.288-0.385-0.556-0.78-0.863-1.155 c-0.639-0.776-1.311-1.522-2.021-2.235c-0.027-0.03-0.049-0.059-0.078-0.089c-0.034-0.034-0.072-0.061-0.108-0.095 c-0.706-0.702-1.446-1.37-2.214-2.002c-0.379-0.309-0.776-0.579-1.163-0.869c-0.44-0.33-0.871-0.67-1.33-0.977 c-0.459-0.307-0.935-0.573-1.406-0.854c-0.417-0.25-0.827-0.512-1.256-0.742c-0.478-0.256-0.968-0.472-1.457-0.702 c-0.45-0.214-0.892-0.438-1.355-0.63c-0.474-0.195-0.956-0.353-1.436-0.524c-0.497-0.18-0.99-0.368-1.497-0.524 c-0.476-0.144-0.962-0.25-1.444-0.37c-0.52-0.131-1.034-0.277-1.567-0.383c-0.554-0.11-1.114-0.173-1.673-0.256 c-0.465-0.066-0.922-0.154-1.396-0.201c-1.023-0.101-2.053-0.154-3.081-0.156c-0.017,0-0.034-0.002-0.047-0.002H256.002 c-17.517,0-31.718,14.201-31.718,31.718s14.201,31.718,31.718,31.718h147.706L9.291,457.853c-12.387,12.387-12.387,32.469,0,44.856 c6.194,6.194,14.311,9.289,22.429,9.289s16.236-3.096,22.427-9.291l394.414-394.414v147.705 C448.561,273.515,462.762,287.716,480.279,287.716z"/> </svg></span>` : ""}
          </div>
          <div class="mgcalendar__tooltip-date">
           <b>Date:</b> ${eventDate}
          </div>
          <div class="mgcalendar__tooltip-date">
           <b>Time:</b> ${eventTimeEl.innerHTML}
          </div>
        </div>
        <div class="mgcalendar__tooltip-content">
          ${
            eventContent
              ? `<div class="mgcalendar__tooltip-description">${eventContent}</div>`
              : ""
          }
          <a href="${arg.event.url}" target="_blank" rel="noopener noreferrer">More details</a>
        </div>
      </div>
      `;

      const listWeekEntry = document.createElement("div");
      if (!isNationalHoliday) {
        listWeekEntry.innerHTML = `
        <a href="${arg.event.url}" class="mgcalendar__list-item" target="_blank" rel="noopener noreferrer">
            ${
              featuredImage || salesForceImage
                ? `<img class="mgcalendar__list-item-image" src="${
                    featuredImage || salesForceImage
                  }" />`
                : ""
            }
            <div class="mgcalendar__list-item-content">
              <div class="mgcalendar__list-item-date">
                ${eventDate}
                ${category ? `<span class="u-ml3">${category}</span>` : ""}
              </div>
              <div class="mgcalendar__list-item-title">${eventTitle}</div>
              ${
                eventContent
                  ? `<div class="mgcalendar__tooltip-description">${eventContent}<span class="mgcalendar__list-item-read-more">Read more »</span></div>`
                  : ""
              }
            </div>
        </a>
        `;
      }

      const arrayOfDomNodes =
        viewType === "dayGridMonth"
          ? [monthViewEntry, toolTip]
          : [listWeekEntry];
      return { domNodes: arrayOfDomNodes };
    },
    eventDidMount: function(info) {
      // Remove absolute positioning from FullCalendar's event harness
      const harness = info.el.closest('.fc-daygrid-event-harness-abs');
      if (harness) {
        harness.classList.remove('fc-daygrid-event-harness-abs');
      }
    },
    viewDidMount: function () {
      // Only initialize filters once
      if (filtersInitialized) {
        return;
      }
      filtersInitialized = true;
      
      let originalEvents = [];
      let originalEventsSet = false; // Flag to ensure we only set original events once

      // Function to reset filters and refresh original events
      function resetFilters() {
        selectedRegion = 'all';
        selectedCategory = 'all';
        
        // When both filters are "all", just refetch current month events
        // This is much faster than processing all events from all months
        calendar.refetchEvents();
      }

      // Function to create and insert filters
      function createFilters(regionsData, categoriesData) {
        // Get .fc-regionFilter-button element
        const regionFilterButton = document.querySelector(
          ".fc-regionFilter-button"
        );

        // Hide the original button completely
        if (regionFilterButton) {
          regionFilterButton.style.display = 'none';
        }

        // Create region filter select element
        const regionFilterSelect = document.createElement("select");
        regionFilterSelect.classList.add("mgcalendar__region-filter");
        regionFilterSelect.id = "mgcalendar__region-filter";
        regionFilterSelect.name = "mgcalendar__region-filter";

        // Create option element for "All" option
        const allOption = document.createElement("option");
        allOption.value = "all";
        allOption.innerText = "All Regions";
        if (selectedRegion === 'all') {
          allOption.selected = true;
        }
        regionFilterSelect.appendChild(allOption);

        // Loop through all regions and create an option element for each region
        regionsData.forEach((region) => {
          const option = document.createElement("option");
          option.value = region.id;
          option.innerText = region.name;
          if (String(selectedRegion) === String(region.id)) {
            option.selected = true;
          }
          regionFilterSelect.appendChild(option);
        });

        // Create category filter select element
        const categoryFilterSelect = document.createElement("select");
        categoryFilterSelect.classList.add("mgcalendar__category-filter");
        categoryFilterSelect.id = "mgcalendar__category-filter";
        categoryFilterSelect.name = "mgcalendar__category-filter";

        // Create option element for "All" option
        const allCategoryOption = document.createElement("option");
        allCategoryOption.value = "all";
        allCategoryOption.innerText = "All Programs";
        if (selectedCategory === 'all') {
          allCategoryOption.selected = true;
        }
        categoryFilterSelect.appendChild(allCategoryOption);

        // Loop through all categories and create an option element for each category
        categoriesData.forEach((category) => {
          const option = document.createElement("option");
          option.value = category.id;
          option.innerText = category.name;
          if (String(selectedCategory) === String(category.id)) {
            option.selected = true;
          }
          categoryFilterSelect.appendChild(option);
        });

        // Create the complete filter container
        const filterContainer = document.createElement("div");
        filterContainer.classList.add("mgcalendar__filters-container");
        filterContainer.innerHTML = `
          <div class="mgcalendar__region-filter-wrapper">
            <label for="mgcalendar__region-filter" class="mgcalendar__region-filter-label">
              Filter by Region:
            </label>
            ${regionFilterSelect.outerHTML}
          </div>
          <div class="mgcalendar__category-filter-wrapper">
            <label for="mgcalendar__category-filter" class="mgcalendar__category-filter-label">
              Filter by Program:
            </label>
            ${categoryFilterSelect.outerHTML}
          </div>
        `;

        // Replace the original button with our complete filter container
        if (regionFilterButton) {
          regionFilterButton.outerHTML = filterContainer.outerHTML;
        }

        // Add event listeners after the elements are in the DOM
        const regionSelectElement = document.getElementById("mgcalendar__region-filter");
        const categorySelectElement = document.getElementById("mgcalendar__category-filter");

        // Add event listener to region filter select
        if (regionSelectElement) {
          regionSelectElement.addEventListener("change", function () {
            selectedRegion = this.value;
            if (selectedRegion === 'all' && selectedCategory === 'all') {
              resetFilters();
            } else {
              applyFilters();
            }
          });
        }

        // Add event listener to category filter select
        if (categorySelectElement) {
          categorySelectElement.addEventListener("change", function () {
            selectedCategory = this.value;
            if (selectedRegion === 'all' && selectedCategory === 'all') {
              resetFilters();
            } else {
              applyFilters();
            }
          });
        }
      }

      // Fetch both regions and categories data simultaneously
      Promise.all([
        fetch("//" + window.location.host + "/wp-json/custom/v1/calendar-regions").then(response => response.json()),
        fetch("//" + window.location.host + "/wp-json/custom/v1/calendar-categories").then(response => response.json())
      ])
      .then(([regionsData, categoriesData]) => {
        createFilters(regionsData, categoriesData);
      })
      .catch((error) => {
        console.error("Error fetching filter data:", error);
      });

      // Initilize tooltip
      setTimeout(() => {
        //initTooltip();
        ensureTableHeaders();
        
        // Clean up any remaining anchor links
        cleanupAnchorLinks();
        
        // Make only external event links open in new tabs
        const eventLinks = document.querySelectorAll('.fc-event');
        eventLinks.forEach(eventLink => {
          if (eventLink.tagName === 'A') {
            // Check if this event has the external property by looking for the external icon
            const externalIcon = eventLink.querySelector('.mgcalendar__tooltip-external');
            if (externalIcon) {
              eventLink.setAttribute('target', '_blank');
              eventLink.setAttribute('rel', 'noopener noreferrer');
            }
          }
        });
      }, 500);
    },
    eventsSet: function () {
      // Safety check: hide any reappearing region buttons
      const regionButtons = document.querySelectorAll('.fc-regionFilter-button');
      regionButtons.forEach(button => {
        button.style.display = 'none';
      });

      // Apply existing filters if any are active and we're not currently applying filters
      if ((selectedRegion !== 'all' || selectedCategory !== 'all') && !isApplyingFilters) {
        applyFilters();
      }
      
      // Note: Removed problematic code that was hiding the last week of some months
      // This was causing months to appear to be missing a full week when the last week
      // contained days from the next month (which have .fc-day-other class)
      
      // Ensure proper table headers are maintained
      ensureTableHeaders();
      
      // Clean up any anchor links that might have been recreated
      cleanupAnchorLinks();
      
      // Make only external event links open in new tabs
      const eventLinks = document.querySelectorAll('.fc-event');
      eventLinks.forEach(eventLink => {
        if (eventLink.tagName === 'A') {
          // Check if this event has the external property by looking for the external icon
          const externalIcon = eventLink.querySelector('.mgcalendar__tooltip-external');
          if (externalIcon) {
            eventLink.setAttribute('target', '_blank');
            eventLink.setAttribute('rel', 'noopener noreferrer');
          }
        }
      });
    },
    
    // Ensure table headers are properly set when view changes
    datesSet: function() {
      // Ensure table headers are properly set after date changes
      setTimeout(() => {
        ensureTableHeaders();
      }, 100);
    },
  });

  try {
    calendar.render();
    // Ensure proper table headers after rendering
    setTimeout(() => {
      ensureTableHeaders();
    }, 100);
  } catch (error) {
    console.error('Error rendering calendar:', error);
    // Try to clean up and re-render
    setTimeout(() => {
      try {
        calendar.render();
        // Ensure proper table headers after re-rendering
        setTimeout(() => {
          ensureTableHeaders();
        }, 100);
      } catch (retryError) {
        console.error('Failed to render calendar after retry:', retryError);
      }
    }, 100);
  }
}
